/**
 * Nbyula brand design tokens.
 *
 * Colors, fonts, and shadows aligned with the Nbyula brand guidelines.
 * These are also defined in tailwind.config.ts — this file provides
 * them as JS constants for inline styles and dynamic usage.
 */

export const brand = {
  colors: {
    jove: {
      10: "#FAF9FF",
      30: "#F4F1FE",
      50: "#EDE8FD",
      60: "#CAB9F8",
      80: "#9574F1",
      DEFAULT: "#5F2EEA",
      200: "#3610A2",
      400: "#1F095D",
    },
    space: {
      20: "#F8F8F8",
      30: "#E5E4E7",
      40: "#BCBCC2",
      50: "#7A7986",
      DEFAULT: "#18181B",
    },
    oxy: { DEFAULT: "#0FC3BD", 50: "#E7FDFC" },
    hydra: { DEFAULT: "#EB1771", 50: "#FFF0F0" },
    gold: { DEFAULT: "#EBBA0B" },
  },
  fonts: {
    sans: ["Inter", "system-ui", "sans-serif"],
  },
  shadows: {
    modal: "0px 25px 80px rgba(17,17,17,0.3)",
    card: "0px 6px 35px rgba(0,28,100,0.1)",
  },
} as const;

/** Risk level → Tailwind color class mapping */
export const riskColors: Record<string, { bg: string; text: string; border: string }> = {
  // Legacy lowercase keys
  compliant: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-300" },
  high_risk: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-300" },
  hard_reject: { bg: "bg-red-50", text: "text-red-700", border: "border-red-300" },
  unverified: { bg: "bg-gray-50", text: "text-gray-600", border: "border-gray-300" },
  // New uppercase keys from pipeline schema
  AMBITIOUS: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-300" },
  TARGET: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-300" },
  SAFETY: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-300" },
  HARD_REJECT: { bg: "bg-red-50", text: "text-red-700", border: "border-red-300" },
};

/** Tool name → icon + label */
export const toolMeta: Record<string, { icon: string; label: string }> = {
  search_programs: { icon: "🔍", label: "Searching database" },
  get_program_details: { icon: "📋", label: "Getting program details" },
  deep_research: { icon: "🌐", label: "Researching programs" },
  quick_search: { icon: "⚡", label: "Quick fact check" },
  fetch_and_verify: { icon: "✅", label: "Verifying via WebSearch" },
  validate_urls: { icon: "🔗", label: "Checking links" },
  extract_pdf_text: { icon: "📄", label: "Reading PDF" },
  get_program_facets: { icon: "📊", label: "Discovering filter values" },
  ToolSearch: { icon: "🔍", label: "Looking up tools" },
  Skill: { icon: "⚙️", label: "Running skill" },
  Agent: { icon: "🤖", label: "Running sub-agent" },
  // ``Task`` is the SDK's real sub-agent-spawning tool. It's now the canonical
  // sub-agent card (we no longer synthesize a duplicate "Agent" row); ``Agent``
  // stays for backward-compatible rendering of pre-existing history rows.
  Task: { icon: "🤖", label: "Running sub-agent" },
  AskUserQuestion: { icon: "💬", label: "Needs further clarification" },
  Read: { icon: "📖", label: "Reading file" },
  Write: { icon: "✏️", label: "Writing file" },
  WebFetch: { icon: "🌍", label: "Fetching page" },
  WebSearch: { icon: "🔎", label: "Searching web" },
  Bash: { icon: "💻", label: "Running command" },
  Glob: { icon: "📁", label: "Finding files" },
  Grep: { icon: "🔍", label: "Searching code" },
  query_database: { icon: "🧠", label: "Searching memory" },
  list_tables: { icon: "🧠", label: "Exploring memory" },
  get_document_url: { icon: "📥", label: "Fetching document" },
  upload_user_document: { icon: "📤", label: "Uploading document" },
  add_artifact: { icon: "📌", label: "Adding artifact" },
  download_and_add_artifact: { icon: "📥", label: "Downloading file" },
  get_user_shortlist: { icon: "📋", label: "Viewing student's shortlist" },
  get_user_documents: { icon: "📎", label: "Viewing student's documents" },
  get_user_application_profile: { icon: "👤", label: "Viewing student's profile" },
  search_universities: { icon: "🏛️", label: "Searching universities" },
  create_university: { icon: "🏛️", label: "Creating university" },
  update_university: { icon: "🏛️", label: "Updating university" },
  create_program: { icon: "📝", label: "Creating program" },
  update_program: { icon: "📝", label: "Updating program" },
  add_to_shortlist: { icon: "⭐", label: "Adding to shortlist" },
  render_ui: { icon: "🎨", label: "Rendering widget" },
};
